import React from "react";
import TableHeaderRow from "../../ui/TableHeaderRow";
import TableHeader from "../../ui/TableHeader";
import Table, { Row, Cell, NoDataCell } from "../../ui/Table";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { selectAdvancedMode } from "../settings/slice";
import Timestamp from "../../ui/Timestamp";
import RSOInfo from "../rsos/RSOInfo";
import SortHeader from "../sorting/SortHeader";
import DropDownFilter from "../filters/DropDownFilter";
import { selectAstrositesById } from "../astrosites/slice";
import PassesDateTimeFilter from "./PassesDateTimeFilter";
import toDegrees from "../../conversions/radiansToDegrees";
import AstrositeName from "../astrosites/AstrositeName";
import { orbitTypes } from "../rsos/orbit";

const PassesTable = ({
	passes = [],
	filter = {},
	setFilter = () => {},
	stickyHeader = true,
	hideFilters = false,
	hideObject = false,
	selectedPasses = [],
	toggleSelectedPass,
	setSelectedPasses,
}) => {
	// User setting to show more details such as id's
	const advancedMode = useSelector(selectAdvancedMode);
	const astrositesByIds = useSelector(selectAstrositesById);

	const allSelected =
		passes?.length > 0 && selectedPasses?.length == passes?.length;
	const onSelectAll = () => {
		setSelectedPasses(allSelected ? [] : passes);
	};

	return (
		<Table>
			<TableHeaderRow sticky={stickyHeader}>
				{toggleSelectedPass && (
					<TableHeader>
						<input
							type="checkbox"
							checked={allSelected}
							onChange={onSelectAll}
							disabled={passes?.length == 0}
						/>
					</TableHeader>
				)}
				{!hideObject && (
					<TableHeader
						sortControl={
							<SortHeader
								setFilter={setFilter}
								filter={filter}
								sortBy={"object_id"}
							/>
						}
					>
						Object
					</TableHeader>
				)}

				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={Object.keys(astrositesByIds).map((a) => ({
									name: a,
									value: a,
								}))}
								filterByName="astrosite_id"
								filter={filter}
								setFilter={setFilter}
								active={filter.astrosite_id ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						<SortHeader
							setFilter={setFilter}
							filter={filter}
							sortBy={"astrosite"}
						/>
					}
				>
					Astrosite
				</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<PassesDateTimeFilter
								filter={filter}
								setFilter={setFilter}
								active={filter.from || filter.to ? true : false}
							/>
						)) ||
						undefined
					}
					sortControl={
						<SortHeader
							setFilter={setFilter}
							filter={filter}
							sortBy={"start_date"}
						/>
					}
				>
					From
				</TableHeader>
				<TableHeader>To</TableHeader>
				<TableHeader
					sortControl={
						<SortHeader
							setFilter={setFilter}
							filter={filter}
							sortBy={"generated_at"}
						/>
					}
				>
					Pass generated at
				</TableHeader>
				<TableHeader>TLE Age</TableHeader>
				<TableHeader
					filterControl={
						(!hideFilters && (
							<DropDownFilter
								values={orbitTypes.map((a) => ({
									name: a,
									value: a,
								}))}
								filterByName="orbit_regime"
								filter={filter}
								setFilter={setFilter}
								active={filter.orbit_regime ? true : false}
							/>
						)) ||
						undefined
					}
				>
					Orbit regime
				</TableHeader>
				<TableHeader>Max elevation</TableHeader>
				<TableHeader>Min solar phase</TableHeader>
				{/*Actions*/}
				<TableHeader />
			</TableHeaderRow>
			{passes && (
				<tbody>
					{passes.map((pass) => (
						<Row key={pass.id} zebra>
							{toggleSelectedPass && (
								<Cell>
									<input
										type="checkbox"
										checked={selectedPasses.includes(pass)}
										onChange={() => toggleSelectedPass(pass)}
									/>
								</Cell>
							)}
							{!hideObject && (
								<Cell>
									<RSOInfo
										key={pass.norad_id || pass.object_id}
										id={pass.norad_id || pass.object_id}
										object_name={pass.object_name}
										object_id={pass.object_id}
									/>
								</Cell>
							)}
							<Cell>
								<AstrositeName astrositeId={pass.astrosite} />
							</Cell>
							<Cell>
								<Timestamp utc={advancedMode} columnMode={true}>
									{pass.start_date}
								</Timestamp>
							</Cell>
							<Cell>
								<Timestamp utc={advancedMode} columnMode={true}>
									{pass.end_date}
								</Timestamp>
							</Cell>
							<Cell>
								<Timestamp utc={advancedMode} columnMode={true}>
									{pass.generated_at}
								</Timestamp>
							</Cell>
							<Cell>
								{pass.tle_epoch && (
									<Timestamp utc={advancedMode} columnMode={true}>
										{pass.tle_epoch}
									</Timestamp>
								)}
							</Cell>
							<Cell>{pass.orbit_regime}</Cell>
							<Cell>
								{pass.max_elevation && toDegrees(pass.max_elevation).toFixed(4)}
								&deg;
							</Cell>
							<Cell>
								{pass.min_solar_phase &&
									toDegrees(pass.min_solar_phase).toFixed(4)}
								&deg;
							</Cell>
						</Row>
					))}
				</tbody>
			)}
			{passes != null && passes.length === 0 && (
				<Row>
					<NoDataCell colSpan="100%">No matching data</NoDataCell>
				</Row>
			)}
		</Table>
	);
};

PassesTable.propTypes = {
	passes: PropTypes.array, // the accounts to render
	filter: PropTypes.object, // If provided will be used to power the filters if not filters will not be shown
	setFilter: PropTypes.func, // If provided will be used to power the filters if not filters will not be shown
	stickyHeader: PropTypes.bool, // If true, table header is sticky
	hideFilters: PropTypes.bool, // If provided hides the filters for the table
	hideObject: PropTypes.bool, // If provided hides the object column
	selectedPasses: PropTypes.array, // If the list item is select it will appear in this list
	toggleSelectedPass: PropTypes.func, // called when an item is selected - toggles inclusion in the list
	setSelectedPasses: PropTypes.func, // sets (replaces) the selected passes
};

export default PassesTable;
